import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { TRANSLATION } from '../../constants'
import CompletedImg from '../../elements/SuccessBox/assets/completed.png'
import { Service } from '../../../types'
import InitialInterverStrategyImg from './assets/initial-interview-strategy.png'
import InterviewStrategyImg1 from './assets/interview-strategy-1.png'
import InterviewStrategyImg2 from './assets/interview-strategy-2.png'
import InterviewStrategyImg3 from './assets/interview-strategy-3.png'
import InterviewStrategyImg4 from './assets/interview-strategy-4.png'
import InterviewStrategyMobileImg1 from './assets/interview-strategy-mobile-1.png'
import InterviewStrategyMobileImg2 from './assets/interview-strategy-mobile-2.png'
import InterviewStrategyMobileImg3 from './assets/interview-strategy-mobile-3.png'
import InterviewStrategyMobileImg4 from './assets/interview-strategy-mobile-4.png'

const actionLinksPerStep = [
  '/career-plans/execute-a-job-search?cp_top_level=4&cp_second_level=11',
  '/career-plans/execute-a-job-search?cp_top_level=4&cp_second_level=12',
  '/career-plans/execute-a-job-search?cp_top_level=4&cp_second_level=13',
  '/career-plans/execute-a-job-search?cp_top_level=4&cp_second_level=14',
]

const imagesPerStep = [
  InterviewStrategyImg1,
  InterviewStrategyImg2,
  InterviewStrategyImg3,
  InterviewStrategyImg4,
]
const imagesPerStepMobile = [
  InterviewStrategyMobileImg1,
  InterviewStrategyMobileImg2,
  InterviewStrategyMobileImg3,
  InterviewStrategyMobileImg4,
]
const initialTranslation = `${TRANSLATION}.interview_strategy.initial`
const progressTranslation = `${TRANSLATION}.interview_strategy.progress`
const completedTranslation = `${TRANSLATION}.interview_strategy.completed`

/**
 * Generates the configuration for the Job Strategy widget based on a progress percentage.
 *
 * - Clamps the input progress between 0 and 100.
 * - If progress is 0, returns the “initial” state (initial text, image, alt text, and link).
 * - If progress is 100, returns the “completed” state (completed text, image, alt text, and link).
 * - Otherwise, divides progress into four 25% steps to pick the correct image and link.
 */
export function useInterviewStrategyConfig(props: { service: Service }) {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const { service } = props

  const percentageProgress = Math.max(
    0,
    Math.min(100, service.calculatedProgress?.percentageProgress || 0),
  )
  const progress = service.progress
  const target = service.data?.target_score

  const initialText = {
    badge: i18n.t(`${initialTranslation}.badge`),
    header: i18n.t(`${initialTranslation}.header`),
    subHeader: i18n.t(`${initialTranslation}.sub_header`),
    actionText: i18n.t(`${initialTranslation}.action_text`),
  }

  const progressText = {
    badge: i18n.t(`${progressTranslation}.badge`),
    header: i18n.t(`${progressTranslation}.header`),
    subHeader: i18n.t(`${progressTranslation}.sub_header`),
    actionText: i18n.t(`${progressTranslation}.action_text`),
  }

  const completedText = {
    badge: undefined,
    header: i18n.t(`${completedTranslation}.header`),
    subHeader: i18n.t(`${completedTranslation}.sub_header`),
    actionText: i18n.t(`${completedTranslation}.action_text`),
  }

  if (progress === 0) {
    return {
      text: initialText,
      status: 'initial',
      image: InitialInterverStrategyImg,
      imageAlt: i18n.t(`${initialTranslation}.image_alt`),
      actionLink: actionLinksPerStep[0],
    }
  }

  if (target && progress >= target) {
    return {
      text: completedText,
      status: 'completed',
      image: CompletedImg,
      imageAlt: i18n.t(`${completedTranslation}.image_alt`),
      actionLink: service.getNextActionUrl() ?? '#',
    }
  }

  const step = Math.min(3, Math.floor((percentageProgress - 1) / 25))

  return {
    text: progressText,
    status: 'progress',
    image: isPhone ? imagesPerStepMobile[step] : imagesPerStep[step],
    imageAlt: i18n.t(`${progressTranslation}.image_alt`),
    actionLink: actionLinksPerStep[step],
  }
}
